import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { localizePublicPath } from "@/lib/publicRoutes";
import { trackEvent } from "@/lib/ga4";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  Eye,
  Lightbulb,
  Menu,
  MessageCircleQuestion,
  Search,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Store,
  TrendingUp,
  X,
} from "lucide-react";
import logoOuraTrust from "@/assets/logo-ouratrust-v2.png";
import logoMbfChauffage from "@/assets/logo-mbf-chauffage.webp";
import dashboardScreenshot from "@/assets/dashboard-screenshot-v2.png";
import logoClinique from "@/assets/trust-logos/clinique-dentaire-lumiere.png";
import logoSaveurs from "@/assets/trust-logos/groupe-saveurs.png";
import logoImmo from "@/assets/trust-logos/immo-horizon.png";
import logoGarage from "@/assets/trust-logos/garage-martin.png";
import logoBeaute from "@/assets/trust-logos/atelier-beaute.png";
import logoRivage from "@/assets/trust-logos/hotels-rivage.png";
import logoKine from "@/assets/trust-logos/kine-plus.png";
import logoBoulangerie from "@/assets/trust-logos/boulangerie-doree.png";
import logoFitzone from "@/assets/trust-logos/fitzone.png";
import logoVeteo from "@/assets/trust-logos/veteocare.png";
import logoDuval from "@/assets/trust-logos/duval-conseil.png";
import logoPizza from "@/assets/trust-logos/pizza-napoli.png";
import logoArchi from "@/assets/trust-logos/studio-archi.png";
import logoFleurs from "@/assets/trust-logos/fleurs-jardins.png";
import logoLoft from "@/assets/trust-logos/loft-collaboratif.png";
import { useLanguage } from "@/hooks/useLanguage";
import { Footer } from "@/components/Footer";
import { SocialProofToast } from "@/components/SocialProofToast";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PricingSection } from "@/components/landing/PricingSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    fr: {
      hero: {
        eyebrow: "La plateforme de gestion d'avis pensée pour les pros",
        titleLine1: "Vos avis Google travaillent pour vous.",
        titleLine2: "Ou contre vous.",
        titleLine3: "À vous de décider.",
        subtitle:
          "Que votre note soit à 3,5 ou à 4,8. Que vous soyez débordé, inquiet de vos concurrents ou simplement convaincu que vous pouvez faire mieux — vos avis ont un impact direct sur vos clients, votre visibilité et votre chiffre d'affaires. OuraTrust vous aide à en reprendre le contrôle. Simplement. Rapidement. Sans expertise.",
        primaryCta: "Je reprends le contrôle",
        secondaryCta: "Voir comment ça marche",
        support: "14 jours d'essai gratuit • Sans engagement • Opérationnel en 10 minutes",
        microProofs: [
          "Fonctionne même si vous n'êtes pas à l'aise avec la tech",
          "Réponses assistées par IA — vous validez, vous publiez",
          "Google, Facebook, Trustpilot, TripAdvisor dans un seul espace",
          "Bientôt disponible sur iOS et Android — gérez tout en 1 clic",
        ],
        imageAlt: "Aperçu du cockpit OuraTrust combinant réputation, fiche Google et visibilité locale",
        overlayLeft: "Un espace plus clair pour voir ce qui influence réellement votre réputation et votre présence locale",
        overlayRightText: "Le plan pensé pour les entreprises qui ne veulent plus piloter à l'aveugle",
      },
      platformBar: {
        eyebrow: "Une seule plateforme pour ce qui influence vraiment votre image locale",
        items: [
          "Google",
          "Facebook",
          "Trustpilot",
          "TripAdvisor",
          "Réponses assistées par IA",
          "Visibilité locale",
          "Gestion multi-établissements",
        ],
        note: "Google et Facebook via intégrations officielles quand elles sont disponibles. Trustpilot et TripAdvisor suivis dans le même espace.",
      },
      problem: {
        eyebrow: "Vous vous reconnaissez ?",
        title: "Ce que vivent la plupart de nos clients avant de nous trouver",
        items: [
          {
            emoji: "🕐",
            title: "Je n'ai jamais le temps",
            description: "Vous savez que vos avis comptent. Mais entre vos clients, vos équipes et votre quotidien, répondre aux avis passe toujours en dernier. Résultat : des avis sans réponse qui donnent une mauvaise image.",
          },
          {
            emoji: "⭐",
            title: "Ma note stagne et je ne sais pas pourquoi",
            description: "Vous faites du bon travail. Mais votre note ne bouge pas. Vous ne savez pas ce qui coince, ni par où commencer pour l'améliorer.",
          },
          {
            emoji: "😰",
            title: "Je ne sais jamais quoi répondre",
            description: "Aux avis négatifs vous avez peur de mal réagir. Aux avis positifs vous ne savez pas quoi dire au-delà de \"merci\". Alors vous ne répondez pas. Et ça se voit.",
          },
          {
            emoji: "👀",
            title: "Mes concurrents ont l'air mieux notés",
            description: "Quand un client hésite entre vous et un concurrent, il regarde les avis. Si les leurs semblent plus soignés, plus répondus, plus actifs — il choisit eux. Pas vous.",
          },
          {
            emoji: "🔍",
            title: "Je suis invisible sur Google",
            description: "Votre fiche Google existe. Mais elle ne travaille pas pour vous. Vos avis s'accumulent sans réponse et votre visibilité locale en souffre directement.",
          },
          {
            emoji: "💡",
            title: "J'espère que l'IA peut m'aider",
            description: "Vous avez entendu parler de l'IA, vous sentez que c'est le moment d'en profiter. Mais vous ne voulez pas un outil compliqué que vous n'utiliserez jamais.",
          },
        ],
      },
      solution: {
        eyebrow: "OuraTrust, concrètement",
        title: "Conçu pour que vous l'utilisiez vraiment.",
        titleLine2: "",
        subtitle:
          "OuraTrust est une plateforme simple et intuitive qui centralise vos avis, vous suggère des réponses adaptées grâce à l'IA et vous donne une vision claire de ce qui influence votre réputation locale. Pas besoin d'être expert. Pas besoin d'y passer des heures.",
        pillars: [
          {
            number: "01",
            title: "Centralisez",
            description: "Google, Facebook, Trustpilot, TripAdvisor. Tous vos avis au même endroit, en temps réel. Fini de jongler entre les plateformes.",
          },
          {
            number: "02",
            title: "Répondez vite, bien, sans effort",
            description: "L'IA analyse chaque avis et vous suggère une réponse adaptée, au ton de votre entreprise. Vous relisez, vous validez, vous publiez. En moins de 30 secondes.",
          },
          {
            number: "03",
            title: "Pilotez votre réputation",
            description: "Suivez l'évolution de votre note, identifiez vos points forts, repérez ce qui freine votre progression. Vous savez enfin où vous en êtes et où aller.",
          },
        ],
      },
      simplicity: {
        eyebrow: "Zéro complexité",
        title: "Si vous savez lire un SMS,",
        titleLine2: "vous saurez utiliser OuraTrust.",
        subtitle:
          "On a conçu OuraTrust pour les gens qui ont autre chose à faire que d'apprendre un nouvel outil. Interface claire, actions simples, résultats visibles. Vous êtes opérationnel en moins de 10 minutes.",
        points: [
          "Aucune formation requise",
          "Prise en main en moins de 10 minutes",
          "Interface pensée pour aller à l'essentiel",
          "Support disponible si vous bloquez",
        ],
        mobileTitle: "📱 Bientôt sur iOS et Android",
        mobileDescription:
          "Imaginez gérer tous vos avis depuis votre téléphone, en 1 clic, entre deux clients. C'est exactement ce qu'on prépare. Rejoignez la liste d'attente et soyez parmi les premiers à l'avoir.",
        mobileCta: "Je veux être prévenu en avant-première",
      },
      forWho: {
        eyebrow: "Pour qui",
        title: "Votre réputation se construit en ligne. Autant que ce soit vous qui la construisiez.",
        subtitle:
          "OuraTrust s'adapte aussi bien à un établissement unique qu'à une structure qui doit piloter plusieurs sites et plusieurs équipes.",
        items: [
          {
            title: "Établissement local",
            description: "Pour surveiller les avis, garder un rythme de réponse propre et poser une base sérieuse.",
          },
          {
            title: "Entreprise en croissance",
            description: "Pour relier les avis, la présence locale et les fiches établissement dans un même pilotage.",
          },
          {
            title: "Organisation multi-sites",
            description: "Pour les équipes qui ont besoin de profondeur, d'historique et de gestion multi-établissements.",
          },
        ],
      },
      trustedBy: {
        eyebrow: "Ils nous font confiance",
        title: "Des entreprises qui utilisent déjà OuraTrust",
        subtitle:
          "Pensé pour les pros qui veulent reprendre la main sur leur réputation locale avec un outil simple, sérieux et utile.",
        note: "Exemple d'entreprise utilisant des outils de pilotage, de visibilité et de réputation dans un contexte business réel.",
      },
      pricing: {
        eyebrow: "Tarifs",
        title: "Trois plans pour trois niveaux de contrôle",
        subtitle:
          "Starter pose la base. Pro ouvre le vrai pilotage local. Business apporte la structure nécessaire aux équipes multi-établissements.",
        complementary: {
          title: "Chaque plan ajoute une couche de maîtrise.",
          items: [
            "Starter vous aide à ne plus subir vos avis",
            "Pro vous aide à reprendre la main sur votre présence locale",
            "Business vous aide à installer une vraie structure multi-sites",
          ],
        },
      },
      compliance: {
        eyebrow: "Conformité et transparence",
        title: "Un produit public, lisible et défendable",
        subtitle:
          "OuraTrust rend visibles ses pages clés de fonctionnement, de conformité, de suppression des données et d'usage des données Google API.",
        note: "Google et Facebook utilisent des intégrations officielles quand elles sont disponibles. Les réponses assistées par IA restent validées humainement avant publication.",
        disclaimer: "OuraTrust ne modifie pas, ne supprime pas et ne bloque pas les avis publiés sur les plateformes.",
        links: [
          { to: "/how-it-works", label: "Comment ça marche" },
          { to: "/pricing", label: "Tarifs" },
          { to: "/privacy-policy", label: "Politique de confidentialité" },
          { to: "/terms", label: "Conditions d'utilisation" },
          { to: "/google-data-usage", label: "Utilisation des données Google API" },
          { to: "/data-deletion", label: "Suppression des données" },
          { to: "/legal-notice", label: "Mentions légales" },
          { to: "/about", label: "À propos" },
          { to: "/contact", label: "Contact" },
        ],
      },
      faq: {
        title: "Questions fréquentes",
        subtitle: "Les réponses essentielles avant de choisir votre plan.",
        items: [
          {
            question: "Faut-il être expert pour utiliser OuraTrust ?",
            answer: "Non. Si vous savez envoyer un message et lire une notification, vous saurez utiliser OuraTrust. On l'a conçu pour des gens occupés, pas pour des techniciens.",
          },
          {
            question: "Est-ce que l'IA répond à ma place sans me demander ?",
            answer: "Jamais. L'IA vous suggère une réponse, vous la lisez, vous la modifiez si besoin, et c'est vous qui publiez. Vous gardez le contrôle total, tout le temps.",
          },
          {
            question: "Est-ce utile si j'ai déjà une bonne note ?",
            answer: "Absolument. Une bonne note se perd vite si elle n'est pas entretenue. OuraTrust vous aide à rester actif, à maintenir votre avance et à continuer de progresser.",
          },
          {
            question: "Est-ce que ça m'aidera si ma note est mauvaise ?",
            answer: "Répondre régulièrement et correctement à vos avis améliore la perception de votre établissement et vous aide à maintenir une présence en ligne plus active et plus rassurante. OuraTrust vous aide à créer cette régularité sans effort.",
          },
          {
            question: "Est-ce que ça marche si je suis vraiment débordé ?",
            answer: "C'est exactement pour ça qu'on l'a construit. Deux minutes par jour suffisent. L'IA s'occupe de la partie la plus longue, vous n'avez plus qu'à valider.",
          },
          {
            question: "Je gère plusieurs établissements. OuraTrust peut gérer ça ?",
            answer: "Oui. Vous pilotez tous vos établissements depuis un seul tableau de bord, selon votre plan.",
          },
          {
            question: "L'application mobile, c'est pour quand ?",
            answer: "On y travaille activement. Rejoignez la liste d'attente pour être prévenu en priorité lors du lancement sur iOS et Android.",
          },
        ],
      },
      finalCta: {
        title: "En ce moment, vos clients lisent vos avis.",
        titleLine2: "Qu'est-ce qu'ils voient ?",
        subtitle:
          "Des avis sans réponse. Une note qui stagne. Une fiche moins active que celle de vos concurrents. Ou au contraire : une entreprise qui répond, qui écoute, qui inspire confiance. La différence se joue maintenant. Et elle est plus simple à faire qu'il n'y paraît.",
        primaryCta: "Je reprends le contrôle",
        secondaryCta: "Comparer les offres",
        reassurance: "14 jours d'essai gratuit sur Pro • Sans engagement",
        reassurance2: "Opérationnel en moins de 10 minutes • Bientôt disponible sur iOS et Android",
      },
    },
    en: {
      hero: {
        eyebrow: "The review management platform built for real businesses",
        titleLine1: "Your Google reviews work for you.",
        titleLine2: "Or against you.",
        titleLine3: "You decide.",
        subtitle:
          "Whether your rating is 3.5 or 4.8. Whether you are overwhelmed, worried about competitors, or simply convinced you could do better — your reviews directly affect your customers, your visibility, and your revenue. OuraTrust helps you take back control. Simply. Quickly. Without expertise.",
        primaryCta: "I take back control",
        secondaryCta: "See how it works",
        support: "14-day free trial • No commitment • Ready in 10 minutes",
        microProofs: [
          "Works even if you are not comfortable with tech",
          "AI-assisted replies — you review, you publish",
          "Google, Facebook, Trustpilot, and TripAdvisor in one place",
          "Coming soon to iOS and Android — manage everything in 1 click",
        ],
        imageAlt: "OuraTrust cockpit preview combining reputation, Google Business Profile and local visibility",
        overlayLeft: "A clearer space to see what truly influences your reputation and local presence",
        overlayRightText: "The plan built for businesses that no longer want to manage blindly",
      },
      platformBar: {
        eyebrow: "One platform for what truly shapes your local image",
        items: [
          "Google",
          "Facebook",
          "Trustpilot",
          "TripAdvisor",
          "AI-assisted replies",
          "Local visibility",
          "Multi-location management",
        ],
        note: "Google and Facebook use official integrations when available. Trustpilot and TripAdvisor are monitored in the same space.",
      },
      problem: {
        eyebrow: "Do you recognize yourself?",
        title: "What most of our customers were dealing with before they found us",
        items: [
          {
            emoji: "🕐",
            title: "I never have time",
            description: "You know your reviews matter. But between customers, staff, and daily operations, replying to reviews always comes last. The result: unanswered reviews that leave the wrong impression.",
          },
          {
            emoji: "⭐",
            title: "My rating is stuck and I do not know why",
            description: "You do good work. But your rating does not move. You do not know what is holding it back or where to start improving it.",
          },
          {
            emoji: "😰",
            title: "I never know what to reply",
            description: "With negative reviews, you are afraid of reacting badly. With positive reviews, you do not know what to say beyond \"thank you.\" So you do not reply. And it shows.",
          },
          {
            emoji: "👀",
            title: "My competitors look better rated",
            description: "When a customer hesitates between you and a competitor, they look at reviews. If theirs look more polished, more answered, more active — they choose them. Not you.",
          },
          {
            emoji: "🔍",
            title: "I am invisible on Google",
            description: "Your Google Business Profile exists. But it is not working for you. Reviews pile up unanswered, and your local visibility suffers directly.",
          },
          {
            emoji: "💡",
            title: "I hope AI can help me",
            description: "You have heard about AI, and you feel like now is the right time to use it. But you do not want a complicated tool you will never actually use.",
          },
        ],
      },
      solution: {
        eyebrow: "OuraTrust, in practical terms",
        title: "Designed to be used.",
        titleLine2: "Not just subscribed to.",
        subtitle:
          "OuraTrust is a simple and intuitive platform that centralizes your reviews, suggests relevant replies with AI, and gives you a clear view of what shapes your local reputation. No need to be an expert. No need to spend hours on it.",
        pillars: [
          {
            number: "01",
            title: "Centralize",
            description: "Google, Facebook, Trustpilot, TripAdvisor. All your reviews in one place, in real time. No more jumping between platforms.",
          },
          {
            number: "02",
            title: "Reply fast, reply well, with less effort",
            description: "AI analyzes each review and suggests a reply that matches your business tone. You review it, approve it, publish it. In under 30 seconds.",
          },
          {
            number: "03",
            title: "Manage your reputation",
            description: "Track the evolution of your rating, identify your strengths, and spot what is slowing your progress. You finally know where you stand and where to go next.",
          },
        ],
      },
      simplicity: {
        eyebrow: "Zero complexity",
        title: "If you can read a text message,",
        titleLine2: "you can use OuraTrust.",
        subtitle:
          "We built OuraTrust for people who have better things to do than learn another tool. Clear interface, simple actions, visible results. You are up and running in under 10 minutes.",
        points: [
          "No training required",
          "Setup in under 10 minutes",
          "Interface built around what matters most",
          "Support available if you get stuck",
        ],
        mobileTitle: "📱 Coming soon to iOS and Android",
        mobileDescription:
          "Imagine handling all your reviews from your phone, in 1 click, between customers. That is exactly what we are building. Join the waiting list and be among the first to get it.",
        mobileCta: "Notify me first",
      },
      forWho: {
        eyebrow: "Who it is for",
        title: "Your reputation is being built online. Make sure it's yours.",
        subtitle:
          "OuraTrust works for a single local business as well as for an organization managing multiple locations and teams.",
        items: [
          {
            title: "Local business",
            description: "To monitor reviews, maintain a cleaner reply rhythm, and build a serious foundation.",
          },
          {
            title: "Growing business",
            description: "To connect reviews, local presence, and business profile management in one system.",
          },
          {
            title: "Multi-location organization",
            description: "For teams that need more depth, more history, and multi-location management.",
          },
        ],
      },
      trustedBy: {
        eyebrow: "Trusted by",
        title: "Businesses already using OuraTrust",
        subtitle:
          "Built for professionals who want more control over their local reputation with a tool that stays simple, serious, and useful.",
        note: "Example of a business using tools tied to visibility, reputation, and structured customer operations in a real-world environment.",
      },
      pricing: {
        eyebrow: "Pricing",
        title: "Three plans for three levels of control",
        subtitle:
          "Starter builds the foundation. Pro unlocks real local control. Business adds the structure required for multi-location teams.",
        complementary: {
          title: "Each plan adds a new layer of control.",
          items: [
            "Starter helps you stop suffering through your reviews",
            "Pro helps you take back control of your local presence",
            "Business helps you build a real multi-site structure",
          ],
        },
      },
      compliance: {
        eyebrow: "Compliance and transparency",
        title: "A public, clear, and defensible product",
        subtitle:
          "OuraTrust makes its key pages on product use, compliance, data deletion, and Google API data usage visible and accessible.",
        note: "Google and Facebook use official integrations when available. AI-assisted replies always require human validation before publication.",
        disclaimer: "OuraTrust does not modify, delete, or block reviews published on platforms.",
        links: [
          { to: "/how-it-works", label: "How it works" },
          { to: "/pricing", label: "Pricing" },
          { to: "/privacy-policy", label: "Privacy Policy" },
          { to: "/terms", label: "Terms of Use" },
          { to: "/google-data-usage", label: "Google API Data Usage" },
          { to: "/data-deletion", label: "Data Deletion" },
          { to: "/legal-notice", label: "Legal Notice" },
          { to: "/about", label: "About" },
          { to: "/contact", label: "Contact" },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        subtitle: "The essential answers before choosing your plan.",
        items: [
          {
            question: "Do I need to be an expert to use OuraTrust?",
            answer: "No. If you know how to send a message and read a notification, you can use OuraTrust. We built it for busy people, not for technicians.",
          },
          {
            question: "Does AI ever reply on my behalf without asking me?",
            answer: "Never. AI suggests a reply, you read it, edit it if needed, and you are the one who publishes it. You keep full control, all the time.",
          },
          {
            question: "Is it useful if I already have a good rating?",
            answer: "Absolutely. A good rating can slip fast if you do not maintain it. OuraTrust helps you stay active, protect your lead, and keep improving.",
          },
          {
            question: "Will it help if my rating is low?",
            answer: "Replying regularly and properly to reviews improves how your business is perceived and helps you maintain a more active and reassuring online presence. OuraTrust helps you build that consistency without effort.",
          },
          {
            question: "Will it work if I am really overwhelmed?",
            answer: "That is exactly why we built it. Two minutes a day is enough. AI handles the longest part, you just review and approve.",
          },
          {
            question: "I manage several locations. Can OuraTrust handle that?",
            answer: "Yes. You manage all your locations from one dashboard, depending on your plan.",
          },
          {
            question: "When is the mobile app coming?",
            answer: "We are actively working on it. Join the waiting list to be notified first when it launches on iOS and Android.",
          },
        ],
      },
      finalCta: {
        title: "Right now, your customers are reading your reviews.",
        titleLine2: "What do they see?",
        subtitle:
          "Unanswered reviews. A rating that is stuck. A profile less active than your competitors'. Or the opposite: a business that replies, listens, and inspires trust. The difference starts now. And it is easier to make than you think.",
        primaryCta: "I take back control",
        secondaryCta: "Compare plans",
        reassurance: "14-day free trial on Pro • No commitment",
        reassurance2: "Ready in under 10 minutes • Coming soon to iOS and Android",
      },
    },
  };

  const page = content[language];
  const problemIcons = [Clock, Star, MessageCircleQuestion, Eye, Search, Lightbulb];
  const solutionIcons = [Store, Sparkles, TrendingUp];
  const forWhoIcons = [Store, TrendingUp, Building2];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="home" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "OuraTrust",
            url: "https://ouratrust.com",
            logo: "https://ouratrust.com/logo-ouratrust-new.svg",
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faq.items.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <SocialProofToast />

      {/* ─── HEADER ─── */}
      <header className="fixed left-0 right-0 top-0 z-50 glass-nav">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link to={localizePublicPath("/", language)} className="flex items-center">
            <img src={logoOuraTrust} alt="OuraTrust" className="h-6 object-contain" width={158} height={24} />
          </Link>

          <div className="hidden items-center gap-2 lg:flex">
            <Link to={localizePublicPath("/how-it-works", language)}>
              <Button variant="ghost" className="text-muted-foreground">
                {language === "fr" ? "Comment ça marche" : "How it works"}
              </Button>
            </Link>
            <Link to={localizePublicPath("/pricing", language)}>
              <Button variant="ghost" className="text-muted-foreground">
                {language === "fr" ? "Tarifs" : "Pricing"}
              </Button>
            </Link>
            <Link to={localizePublicPath("/about", language)}>
              <Button variant="ghost" className="text-muted-foreground">
                {language === "fr" ? "À propos" : "About"}
              </Button>
            </Link>
            <Link to={localizePublicPath("/blog", language)}>
              <Button variant="ghost" className="text-muted-foreground">
                Blog
              </Button>
            </Link>
            <Link to={localizePublicPath("/contact", language)}>
              <Button variant="ghost" className="text-muted-foreground">
                Contact
              </Button>
            </Link>
          </div>

          <div className="hidden items-center gap-2 lg:flex lg:gap-3">
            <LanguageSwitcher />
            <Link to="/login">
              <Button variant="ghost" className="hidden text-muted-foreground lg:inline-flex">
                {language === "fr" ? "Connexion" : "Log in"}
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="shadow-button">{language === "fr" ? "S'inscrire" : "Sign up"}</Button>
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-border bg-background/95 backdrop-blur-md lg:hidden"
            >
              <div className="container mx-auto flex flex-col gap-2 px-4 py-4">
                <Link to={localizePublicPath("/how-it-works", language)} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {language === "fr" ? "Comment ça marche" : "How it works"}
                  </Button>
                </Link>
                <Link to={localizePublicPath("/pricing", language)} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {language === "fr" ? "Tarifs" : "Pricing"}
                  </Button>
                </Link>
                <Link to={localizePublicPath("/about", language)} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {language === "fr" ? "À propos" : "About"}
                  </Button>
                </Link>
                <Link to={localizePublicPath("/blog", language)} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    Blog
                  </Button>
                </Link>
                <Link to={localizePublicPath("/contact", language)} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    Contact
                  </Button>
                </Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {language === "fr" ? "Connexion" : "Log in"}
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full shadow-button">{language === "fr" ? "S'inscrire" : "Sign up"}</Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        {/* ─── 1. HERO ─── */}
        <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-muted/30 to-background pt-32 pb-20 md:pt-36 md:pb-24">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute right-0 top-24 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
          </div>
          <div className="container relative mx-auto px-4">
            <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge variant="secondary" className="mb-6 rounded-full px-4 py-1.5 text-xs font-medium">
                  {page.hero.eyebrow}
                </Badge>
                <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-foreground md:text-5xl lg:text-6xl md:leading-[1.01]">
                  {page.hero.titleLine1}
                  <br />
                  <span className="text-primary">{page.hero.titleLine2}</span>
                  <br />
                  {page.hero.titleLine3}
                </h1>
                <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground md:text-xl">
                  {page.hero.subtitle}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="btn-premium h-13 px-7 text-base" onClick={() => trackEvent("click_hero_primary", { language, page_type: "home" })}>
                    <Link to="/signup?plan=pro">
                      {page.hero.primaryCta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-13 px-7 text-base" onClick={() => trackEvent("click_hero_secondary", { language, page_type: "home" })}>
                    <Link to={localizePublicPath("/how-it-works", language)}>{page.hero.secondaryCta}</Link>
                  </Button>
                </div>

                <p className="mt-4 text-sm text-muted-foreground">{page.hero.support}</p>

                <div className="mt-8 space-y-3">
                  {page.hero.microProofs.map((proof) => (
                    <div key={proof} className="flex items-start gap-2.5">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span className="text-sm text-foreground">{proof}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="relative hidden lg:block"
              >
                <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card shadow-2xl">
                  <img
                    src={dashboardScreenshot}
                    alt="OuraTrust Dashboard"
                    className="block w-full rounded-[2rem]"
                    width={800}
                    height={600}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── 2. PLATFORM PROOF BAR ─── */}
        <section className="border-b border-border bg-muted/30 py-8">
          <div className="container mx-auto px-4">
            <div className="landing-panel rounded-[2rem] px-5 py-5 md:px-6 md:py-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {page.platformBar.eyebrow}
                </p>
                <div className="flex flex-wrap gap-3">
                  {page.platformBar.items.map((item) => (
                    <div
                      key={item}
                      className="inline-flex items-center gap-2.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground"
                    >
                      <CheckCircle2 size={16} className="shrink-0 text-primary" strokeWidth={2.2} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">{page.platformBar.note}</p>
            </div>
          </div>
        </section>

        {/* ─── 3. THE PROBLEM ─── */}
        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.problem.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.problem.title}
              </h2>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {page.problem.items.map((item, index) => {
                const Icon = problemIcons[index];
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.06 }}
                    className="landing-card rounded-3xl p-8"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl" role="img" aria-hidden="true">{item.emoji}</span>
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                        <Icon className="h-4.5 w-4.5 text-destructive" />
                      </div>
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── 4. THE SOLUTION ─── */}
        <section className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.solution.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.solution.title}
                <br />
                {page.solution.titleLine2}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.solution.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {page.solution.pillars.map((pillar, index) => {
                const Icon = solutionIcons[index];
                return (
                  <motion.div
                    key={pillar.number}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="landing-card rounded-3xl p-8"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">{pillar.number}</span>
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-foreground">{pillar.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{pillar.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── 5. SIMPLICITY ─── */}
        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="secondary" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.simplicity.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.simplicity.title}
                <br />
                {page.simplicity.titleLine2}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.simplicity.subtitle}</p>
            </div>

            <div className="mx-auto mt-10 max-w-2xl space-y-4">
              {page.simplicity.points.map((point) => (
                <div key={point} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-accent" />
                  <span className="text-base text-foreground">{point}</span>
                </div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto mt-12 max-w-2xl rounded-[2rem] border border-primary/20 bg-primary/5 p-5 sm:p-8"
            >
              <div>
                <h3 className="text-lg font-semibold text-foreground">{page.simplicity.mobileTitle}</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground sm:text-base">{page.simplicity.mobileDescription}</p>
                <Button asChild variant="outline" size="default" className="mt-5 w-full sm:w-auto sm:size-lg" onClick={() => trackEvent("click_mobile_waitlist", { language, page_type: "home" })}>
                  <Link to="/signup">{page.simplicity.mobileCta}</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── 6. FOR WHO ─── */}
        <section className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.forWho.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.forWho.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.forWho.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {page.forWho.items.map((item, index) => {
                const Icon = forWhoIcons[index];
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="landing-card rounded-3xl p-8"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── 7. TRUSTED BY ─── */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.trustedBy.eyebrow}
              </Badge>
              <h2 className="mt-5 text-2xl font-semibold tracking-tight text-foreground md:text-4xl">
                {page.trustedBy.title}
              </h2>
              <p className="mt-3 text-base leading-7 text-muted-foreground">{page.trustedBy.subtitle}</p>
            </div>

            {(() => {
              const trustLogos = [
                { src: logoOuraTrust, alt: "OuraTrust" },
                { src: logoMbfChauffage, alt: "MBF Chauffage" },
                { src: logoClinique, alt: "Clinique Dentaire Lumière" },
                { src: logoSaveurs, alt: "Groupe Saveurs" },
                { src: logoImmo, alt: "Immo Horizon" },
                { src: logoGarage, alt: "Garage Martin & Fils" },
                { src: logoBeaute, alt: "L'Atelier Beauté" },
                { src: logoRivage, alt: "Hôtels Rivage" },
                { src: logoKine, alt: "Kiné Plus Santé" },
                { src: logoBoulangerie, alt: "Boulangerie Dorée" },
                { src: logoFitzone, alt: "FitZone" },
                { src: logoVeteo, alt: "VétéoCare" },
                { src: logoDuval, alt: "Cabinet Duval Conseil" },
                { src: logoPizza, alt: "Pizza Napoli Express" },
                { src: logoArchi, alt: "Studio Archi Design" },
                { src: logoFleurs, alt: "Fleurs & Jardins" },
                { src: logoLoft, alt: "Le Loft Collaboratif" },
              ];
              return (
                <div className="group relative mx-auto mt-10 overflow-hidden">
                  <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background to-transparent" />
                  <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background to-transparent" />
                  <div className="flex w-max animate-[scroll_60s_linear_infinite] items-center gap-12 group-hover:[animation-play-state:paused]">
                    {[...trustLogos, ...trustLogos].map((logo, i) => {
                      const isFixed = logo.alt === "OuraTrust" || logo.alt === "MBF Chauffage";
                      return (
                        <img
                          key={`${logo.alt}-${i}`}
                          src={logo.src}
                          alt={logo.alt}
                          className={`w-auto shrink-0 object-contain opacity-70 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0 ${isFixed ? "h-[26px] max-h-[26px] max-w-[80px]" : "h-[60px] max-h-[60px] max-w-[187px]"}`}
                          loading="lazy"
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <p className="mx-auto mt-6 max-w-xl text-center text-xs text-muted-foreground">{page.trustedBy.note}</p>
          </div>
        </section>

        {/* ─── 8. TESTIMONIALS ─── */}
        <TestimonialsSection />

        {/* ─── 9. PRICING ─── */}
        <section className="py-20 md:py-24">
          <PricingSection />

          {/* Complementary block */}
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto mt-10 max-w-3xl rounded-2xl border border-border bg-primary/5 p-6 md:p-8"
            >
              <h3 className="text-lg font-semibold text-foreground">{page.pricing.complementary.title}</h3>
              <ul className="mt-4 space-y-3">
                {page.pricing.complementary.items.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span className="text-base text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* ─── 10. COMPLIANCE / TRANSPARENCY ─── */}
        <section className="border-y border-border bg-muted/30 py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              <div className="rounded-[2rem] border border-border bg-card p-8 md:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                    {page.compliance.eyebrow}
                  </Badge>
                </div>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                  {page.compliance.title}
                </h2>
                <p className="mt-3 text-base leading-7 text-muted-foreground">
                  {page.compliance.subtitle}
                </p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {page.compliance.links.map((link) => (
                    <Link
                      key={link.to}
                      to={localizePublicPath(link.to, language)}
                      className="flex items-center gap-2 rounded-2xl border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-primary/5"
                    >
                      {link.label}
                      <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
                <p className="mt-6 text-xs text-muted-foreground">{page.compliance.note}</p>
                <p className="mt-3 rounded-xl border border-border bg-muted/60 px-4 py-3 text-xs font-medium text-muted-foreground">
                  {page.compliance.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── 11. FAQ ─── */}
        <section id="faq" className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                FAQ
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.faq.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.faq.subtitle}</p>
            </motion.div>

            <div className="mx-auto mt-12 max-w-3xl">
              <Accordion type="single" collapsible className="space-y-4">
                {page.faq.items.map((item, index) => (
                  <AccordionItem key={item.question} value={`faq-${index}`} className="landing-card rounded-2xl px-6">
                    <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-7 text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* ─── 12. FINAL CTA ─── */}
        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="overflow-hidden rounded-[2rem] border border-border bg-gradient-to-br from-card via-card to-muted/60 p-8 shadow-xl md:p-12">
              <div className="mx-auto max-w-3xl text-center">
                <Badge className="rounded-full px-4 py-1.5 text-xs font-medium">OuraTrust</Badge>
                <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {page.finalCta.title}
                  <br />
                  {page.finalCta.titleLine2}
                </h2>
                <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.finalCta.subtitle}</p>
                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                  <Button asChild size="lg" className="btn-premium h-13 px-7 text-base" onClick={() => trackEvent("click_start_pro", { language, cta_location: "final_cta", page_type: "home" })}>
                    <Link to="/signup?plan=pro">
                      {page.finalCta.primaryCta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-13 px-7 text-base" onClick={() => trackEvent("click_compare_plans", { language, cta_location: "final_cta", page_type: "home" })}>
                    <Link to={localizePublicPath("/pricing", language)}>{page.finalCta.secondaryCta}</Link>
                  </Button>
                </div>
                <p className="mt-5 text-sm text-muted-foreground">{page.finalCta.reassurance}</p>
                <p className="text-sm text-muted-foreground">{page.finalCta.reassurance2}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
